import api from './api';
import { db } from '../firebase/config';
import { ref, set, onValue, off, get, update } from 'firebase/database';
import { friendEvents } from '../components/SocialMenu';

// Get a reference to the friend status data
const getFriendStatusRef = (walletAddress) => ref(db, `friendStatus/${walletAddress}`);

// Send friend request
export const sendFriendRequest = async (senderWallet, receiverWallet) => {
  try {
    const response = await api.post('/connections/request', {
      senderWallet,
      receiverWallet
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error connecting to server' };
  }
};

// Accept or reject friend request
export const respondToFriendRequest = async (connectionId, status) => {
  try {
    const response = await api.put(`/connections/request/${connectionId}`, { status });
    
    // If the request was accepted, handle the real-time update for both users
    if (status === 'accepted') {
      try {
        // Get the connection details to retrieve the wallet addresses
        const connection = response.data;
        const senderWallet = connection.senderWallet;
        const receiverWallet = connection.receiverWallet;
        
        // Fetch both users' details to get character information
        const [senderResponse, receiverResponse] = await Promise.all([
          api.get(`/users/${senderWallet}`),
          api.get(`/users/${receiverWallet}`)
        ]);
        
        const sender = senderResponse.data;
        const receiver = receiverResponse.data;
        
        // Update Firebase for the receiver (adding sender's info)
        await set(ref(db, `friendStatus/${receiverWallet}/${senderWallet}`), {
          username: sender.username,
          character: sender.character,
          status: 'accepted',
          updatedAt: Date.now()
        });
        
        // Update Firebase for the sender (adding receiver's info)
        await set(ref(db, `friendStatus/${senderWallet}/${receiverWallet}`), {
          username: receiver.username,
          character: receiver.character,
          status: 'accepted',
          updatedAt: Date.now()
        });
        
        // Force update the UI by triggering the friend event system
        console.log('Emitting friend update event after acceptance');
        friendEvents.emit();
      } catch (error) {
        console.error('Error syncing friend data to Firebase:', error);
      }
    }
    
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error connecting to server' };
  }
};

// Get pending friend requests
export const getPendingRequests = async (walletAddress) => {
  try {
    const response = await api.get(`/connections/pending/${walletAddress}`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching pending requests:', error.message, error.response?.data);
    throw error.response?.data || { message: 'Error fetching pending requests' };
  }
};

// Get all friends - Modified to check Firebase first, then fall back to API
export const getFriends = async (walletAddress) => {
  try {
    // First try to get friends from Firebase
    const friendStatusRef = getFriendStatusRef(walletAddress);
    const snapshot = await get(friendStatusRef);
    
    if (snapshot.exists()) {
      const friendsData = [];
      const friendsObj = snapshot.val();
      
      // Process friends from Firebase
      Object.keys(friendsObj).forEach(friendWallet => {
        if (friendsObj[friendWallet].status === 'accepted') {
          friendsData.push({
            walletAddress: friendWallet,
            username: friendsObj[friendWallet].username,
            character: friendsObj[friendWallet].character
          });
        }
      });
      
      console.log('Retrieved friends from Firebase:', friendsData);
      
      if (friendsData.length > 0) {
        return friendsData;
      }
    }
    
    // If no friends in Firebase, fall back to API
    console.log('No friends in Firebase, fetching from API');
    const response = await api.get(`/connections/friends/${walletAddress}`);
    const apiFriends = response.data;
    
    // If we got friends from API, store them in Firebase for future use
    if (apiFriends && apiFriends.length > 0) {
      console.log('Storing API friends in Firebase');
      apiFriends.forEach(async (friend) => {
        await set(ref(db, `friendStatus/${walletAddress}/${friend.walletAddress}`), {
          username: friend.username,
          character: friend.character,
          status: 'accepted',
          updatedAt: Date.now()
        });
      });
    }
    
    return apiFriends;
  } catch (error) {
    console.error('Error in getFriends:', error);
    throw error.response?.data || { message: 'Error connecting to server' };
  }
};

// Subscribe to friend status changes for real-time updates
export const subscribeFriendStatus = (walletAddress, callback) => {
  const friendStatusRef = getFriendStatusRef(walletAddress);
  
  const listener = onValue(friendStatusRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }
    
    const friendsData = [];
    const friendsObj = snapshot.val();
    
    Object.keys(friendsObj).forEach(friendWallet => {
      if (friendsObj[friendWallet].status === 'accepted') {
        friendsData.push({
          walletAddress: friendWallet,
          username: friendsObj[friendWallet].username,
          character: friendsObj[friendWallet].character
        });
      }
    });
    
    callback(friendsData);
  });
  
  // Return unsubscribe function
  return () => off(friendStatusRef, 'value', listener);
};

// Remove a friend
export const removeFriend = async (walletAddress, friendWallet) => {
  try {
    // First delete from the server
    const response = await api.delete(`/connections/friend/${friendWallet}`, {
      data: { walletAddress }
    });
    
    // Then remove from Firebase in both directions
    await set(ref(db, `friendStatus/${walletAddress}/${friendWallet}`), null);
    await set(ref(db, `friendStatus/${friendWallet}/${walletAddress}`), null);
    
    // Emit friend update event
    friendEvents.emit();
    
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error removing friend' };
  }
};

// Get friend count
export const getFriendCount = async (walletAddress) => {
  try {
    const response = await api.get(`/connections/count/${walletAddress}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error fetching friend count' };
  }
};